import TravelPassConsumptionCard from '@/components/manage/travel-passes/TravelPassConsumptionCard';
import ResponsiveDialog from '@/components/ResponsiveDialog';
import {
  getManageFlowBookingById,
  getManageFlowBookingByNumberAndEmail,
  getManageFlowTravelAccountsMap,
} from '@/features/manage/manageActions';
import { manageFlowBookingSelector } from '@/features/manage/manageSelectors';
import { profileDataSelector } from '@/features/profile/profileSelectors';
import { useConsumptionBookingLookup } from '@/hooks/useConsumptionBookingLookup';
import { TransAlert } from '@/i18n/trans/alert';
import { TransText } from '@/i18n/trans/text';
import { useDispatch, useSelector } from '@/store/utils';
import type { TravelAccountConsumptionDTO } from '@/types/dto/agent';
import {
  getBookingNonTripOfferFulfillments,
  isTravelPassBooking,
} from '@/utils/booking';
import { Typography } from '@packages/webshop-design-system';
import type { FC } from 'react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

interface ViewBookedTripsDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  consumptions: TravelAccountConsumptionDTO[];
}

const ViewBookedTripsDialog: FC<ViewBookedTripsDialogProps> = ({
  isOpen,
  onOpenChange,
  consumptions,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loadingBookingRef, setLoadingBookingRef] = useState<string | null>(
    null
  );
  const profile = useSelector(profileDataSelector);
  const manageFlowBooking = useSelector(manageFlowBookingSelector);

  // The dialog is reachable both logged-in (account) and anonymously
  // (manage-booking, authorized by email + booking number) — either way an
  // email scoped to these bookings is available for the lookup.
  const lookupEmail =
    profile?.email ??
    manageFlowBooking?.purchaser?.contactInformation?.emailAddress;

  const bookingSummaries = useConsumptionBookingLookup(
    consumptions.map(({ bookingId }) => bookingId).filter(Boolean),
    lookupEmail,
    isOpen
  );

  const handleViewDetails = useCallback(
    async (bookingRef: string) => {
      if (!bookingRef) return;

      setLoadingBookingRef(bookingRef);

      try {
        // `bookingRef` is the booking NUMBER — it cannot be fetched via
        // GET /bookings/{id} directly (forbidden route). Use the id the
        // card lookup already resolved; fall back to the number + email
        // search flow (the same path the account booking lists use).
        const resolvedId = bookingSummaries[bookingRef]?.id;
        const booking = resolvedId
          ? await dispatch(getManageFlowBookingById(resolvedId)).unwrap()
          : await dispatch(
              getManageFlowBookingByNumberAndEmail({
                bookingNumber: bookingRef,
                email: lookupEmail ?? '',
              })
            ).unwrap();

        if (!booking) {
          toast.error(<TransAlert i18nKey="fetchingBookingFailed" />);
          return;
        }

        const fulfillments = getBookingNonTripOfferFulfillments(booking);
        if (isTravelPassBooking(booking) && fulfillments.length) {
          await dispatch(getManageFlowTravelAccountsMap(fulfillments)).unwrap();
        }

        onOpenChange(false);
        navigate('/manage-booking/details');
      } catch (error) {
        toast.error(<TransAlert i18nKey="fetchingBookingFailed" />);
      } finally {
        setLoadingBookingRef(null);
      }
    },
    [bookingSummaries, dispatch, lookupEmail, navigate, onOpenChange]
  );

  const content =
    consumptions.length === 0 ? (
      <div className="flex items-center justify-center p-8">
        <Typography variant="body1" className="text-neutral">
          <TransText i18nKey="noConsumptionsFound" />
        </Typography>
      </div>
    ) : (
      <div className="flex flex-col gap-4 p-4">
        {consumptions.map((consumption, index) => {
          const isLoading = loadingBookingRef === consumption.bookingId;
          const isDisabled =
            !consumption.bookingId || loadingBookingRef !== null;

          return (
            <TravelPassConsumptionCard
              key={`${consumption.bookingId}-${index}`}
              consumption={consumption}
              resolvedBooking={bookingSummaries[consumption.bookingId] ?? null}
              onViewDetails={handleViewDetails}
              isLoading={isLoading}
              isDisabled={isDisabled}
            />
          );
        })}
      </div>
    );

  return (
    <ResponsiveDialog
      open={isOpen}
      onOpenChange={onOpenChange}
      title={<TransText i18nKey="travelPassUsage" />}
      content={content}
    />
  );
};

export default ViewBookedTripsDialog;
